export const EXPORT_ERROR_CODES = [
  'MEDIA_FETCH_FAILED',
  'REPORT_DATA_INCOMPLETE',
  'ARCHIVE_FAILED',
  'SAVE_FAILED',
  'UNKNOWN',
] as const

export type TExportErrorCode = (typeof EXPORT_ERROR_CODES)[number]

export type TExportError = {
  code: TExportErrorCode
  cause: string
}

export type TExportFailure = {
  code: TExportErrorCode
  alarmId: string
  name?: string
  cause: string
}

const isExportError = (value: unknown): value is TExportError =>
  typeof value === 'object' &&
  value !== null &&
  'code' in value &&
  'cause' in value &&
  (EXPORT_ERROR_CODES as readonly unknown[]).includes(value.code) &&
  typeof value.cause === 'string'

const messageOf = (cause: unknown): string =>
  cause instanceof Error ? cause.message : String(cause)

export const toExportError = (
  cause: unknown,
  fallback: TExportErrorCode,
): TExportError => {
  if (isExportError(cause)) return cause

  return {
    code: fallback,
    cause: messageOf(cause),
  }
}

export const exportErrorMessageKey = (code: TExportErrorCode): string =>
  `alarm_report.export.errors.${code}`

export const dominantFailureCode = (
  failures: TExportFailure[],
): TExportErrorCode | null => {
  const counts = new Map<TExportErrorCode, number>()
  let dominant: TExportErrorCode | null = null
  let dominantCount = 0

  for (const { code } of failures) {
    const count = (counts.get(code) ?? 0) + 1
    counts.set(code, count)

    if (count > dominantCount) {
      dominant = code
      dominantCount = count
    }
  }

  return dominant
}
